import { createSlice, createSelector } from '@reduxjs/toolkit';
import socketFactory, { mapSocketFactory } from '../../../../utils/socketFactory';
import PatchClient from '../../../../utils/PatchClient';
import { updateLocalAvatar } from '../../../Auth/store/authSlice';
import {getAgent} from './AvatarsThunks'
const initialState = {
    avatars: {},
    loading: false,
    error: null,
    agents:[],
};

const avatarSlice = createSlice({
    name: 'avatars',
    initialState,
    reducers: {
        AVATARS_UPDATE_SUCCESS: (state, action) => {
            state.loading = false;
            state.error = null;
            state.avatars = action.payload;
        },
        AVATARS_UPDATE_FAILURE: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        AVATARS_PATCHED: (state, action) => {
            try {
                const newAvatars = action.payload; // Object from repository

                // Update only changed avatars
                Object.entries(newAvatars).forEach(([id, avatar]) => {
                    const existing = state.avatars[id];
                    if (!existing || JSON.stringify(existing) !== JSON.stringify(avatar)) {
                        state.avatars[id] = avatar;
                    }
                });

                // Remove avatars that no longer exist
                Object.keys(state.avatars).forEach(id => {
                    if (!(id in newAvatars)) {
                        delete state.avatars[id];
                    }
                });
                state.loading = false;
                state.error = null;
            }
            catch (error) {
                console.error("Error patching avatars:", error);
                state.error = "Failed to patch avatars";
            }
        },
        AGENTS_IA: (state, action) => {
            const data = action.payload?.data?.agents;
            state.agents = Array.isArray(data) ? data : [];
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAgent.fulfilled, (state, action) => {
                state.agents = action.payload?.data || action.payload;;
                state.loading = false;
                state.error = null;
            })
            .addCase(getAgent.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message || "Failed to fetch agents by map";
                console.error("Failed to fetch agents by map:", action.error);
            });
    },
});

export const {
    AVATARS_UPDATE_SUCCESS,
    AVATARS_UPDATE_FAILURE,
    AVATARS_PATCHED ,
    AGENTS_IA
} = avatarSlice.actions;


export const selectAvatarsArray = createSelector(
    [state => state.avatars.avatars],
    (avatarsObject) => Object.values(avatarsObject)
);
export const selectAgentsArray = createSelector(
    [state => state.avatars.agents],
    (agents) => Array.isArray(agents) ? agents : []
);

// PatchClient instance (will be initialized in startAvatarsSync)
let avatarsPatchClient = null;



// Integrate PatchClient for syncing avatars state
export const startAvatarsSync = () => (dispatch) => {
    console.log("Starting avatars sync...");

    const reinitAvatarsPatchClient = (fullDocument) => {
        avatarsPatchClient = null;
        avatarsPatchClient = new PatchClient(fullDocument, {
            onPatchRequest: (fromVersion) => {
                mapSocketFactory.emit('requestAvatarsPatch', 'avatars/requestPatch')({ fromVersion });
            },
            onFullDocumentRequest: () => {
                mapSocketFactory.emit('requestAvatarsFull', 'avatars/requestFull')();
            },
        });
        dispatch(AVATARS_UPDATE_SUCCESS(fullDocument.data));
        dispatch(updateLocalAvatar(fullDocument.data));
    };

    const requestFullDocument =  mapSocketFactory.emit('requestAvatarsFull', 'avatars/requestFull');

    const onAvatarsFull = async (data) => {
        if ((!avatarsPatchClient || data.firstRequest) && data) {
            console.log("Reinitializing patch client with fullDocument:", data);
            reinitAvatarsPatchClient(data)
        }
        else if (avatarsPatchClient && data) {
            console.log("Updating avatars document with fullDocument:", data);
            const patchSuccess = avatarsPatchClient.receiveFullDocument(data);
            if (patchSuccess) {
                console.log("Avatars document updated successfully");
                dispatch(AVATARS_UPDATE_SUCCESS(data.data));
                dispatch(updateLocalAvatar(data.data));
            } else {
                console.warn("Failed to update avatars document with fullDocument");
                dispatch(AVATARS_UPDATE_FAILURE("Failed to update avatars document with fullDocument"));
            }
        }
        else {
            console.warn("No full document provided to updateDocument");
        }
    }


    const onAvatarsPatched = (patches) => {
        {
            console.log("Patching Avatars", patches);
            if (avatarsPatchClient) {
                const patchSuccess = avatarsPatchClient.applyPatches(patches);
                if (patchSuccess) {
                    console.log("Avatars patched successfully");
                    const updatedAvatars = avatarsPatchClient.getDocument();
                    dispatch(AVATARS_PATCHED(updatedAvatars.data));
                    dispatch(updateLocalAvatar(updatedAvatars.data));
                } else {
                    console.warn("Failed to apply avatars patch");
                    dispatch(AVATARS_UPDATE_FAILURE("Failed to apply avatars patch"));
                }
            } else {
                console.warn("AvatarsPatchClient is not initialized, cannot apply patch");
                dispatch(AVATARS_UPDATE_FAILURE("AvatarsPatchClient is not initialized"));
                dispatch(requestFullDocument());
            }
        }
    }


    mapSocketFactory.on('avatarsPatched', (payload) => onAvatarsPatched(payload.data));
    mapSocketFactory.on('avatarsFull', (payload) => onAvatarsFull(payload.data));
    dispatch(requestFullDocument());
    socketFactory.on(`activeAgentStatusResponse`, AGENTS_IA);

};

export const POKE_USER = socketFactory.emit("pokeUser", "avatars/POKE_USER");
export const FIRE_GUEST = socketFactory.emit("fireGuest", "avatars/FIRE_GUEST");
export const EXCLUDE_USER = mapSocketFactory.emit('excludeUser', 'socket/EXCLUDE_USER');
export const FIRE_GUESTS = socketFactory.emit("fireGuestsConf", "avatars/FIRE_GUEST");


export default avatarSlice.reducer;
